import { cn } from '@/lib/utils';
import type { ChatThread, ThreadVisibility } from '../types';

interface ThreadBadgeBarProps {
  threads: ChatThread[];
  visibility: ThreadVisibility;
  onToggleThread: (threadId: string) => void;
  onToggleAll: () => void;
  onExclusiveThread: (threadId: string) => void;
  className?: string;
}

export function ThreadBadgeBar({
  threads,
  visibility,
  onToggleThread,
  onToggleAll,
  onExclusiveThread,
  className
}: ThreadBadgeBarProps) {
  const hasSelectedThreads = Object.values(visibility).some((v) => v === true);
  const _allSelected =
    hasSelectedThreads && Object.values(visibility).every((v) => v);

  const handleBadgeClick = (e: React.MouseEvent, threadId: string) => {
    e.preventDefault();
    onToggleThread(threadId);
  };

  const handleBadgeLongPress = (threadId: string) => {
    // Long press for exclusive mode
    let pressTimer: NodeJS.Timeout;

    const startPress = () => {
      pressTimer = setTimeout(() => {
        onExclusiveThread(threadId);
      }, 500);
    };

    const cancelPress = () => {
      clearTimeout(pressTimer);
    };

    return {
      onMouseDown: startPress,
      onMouseUp: cancelPress,
      onMouseLeave: cancelPress
    };
  };

  return (
    <div
      className={cn(
        'bg-card sticky top-0 z-50',
        'border-border border-b px-4 py-2',
        className
      )}
    >
      <div className="flex scrollbar-none items-center gap-2 overflow-x-auto">
        {/* All threads badge */}
        <button
          onClick={() => onToggleAll()}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium',
            'cursor-pointer transition-all duration-200 select-none',
            'hover:scale-105 active:scale-95',
            !hasSelectedThreads
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-foreground-secondary hover:bg-muted/90'
          )}
        >
          All
        </button>

        {/* Thread badges */}
        {threads.map((thread) => {
          const isSelected = visibility[thread.id] === true;
          const hasUnread = thread.unreadCount > 0;

          return (
            <button
              key={thread.id}
              onClick={(e) => handleBadgeClick(e, thread.id)}
              {...handleBadgeLongPress(thread.id)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium',
                'cursor-pointer transition-all duration-200 select-none',
                !(isSelected && thread.color) &&
                  'bg-muted text-muted-foreground',
                'hover:scale-105 active:scale-95',
                'group relative',
                !isSelected && hasSelectedThreads && 'opacity-50'
              )}
              /*
                VALE-531: the UNSELECTED chip's fill and ink were `gray-100` and
                `gray-500` written longhand as inline `rgb()`, which no theme
                switch can move — a light-grey pill on a dark ground. They are
                the `muted` pair (Δ2 and Δ13), so they move onto the tokens and
                out of the style block entirely.

                The SELECTED chip keeps its inline style, and deliberately: it is
                painted from `thread.color`, a value that arrives from the DATA
                rather than from source, which is VALE-524's second
                structurally-invisible category. White ink on an arbitrary
                saturated fill is correct in both themes for the same reason.
              */
              style={
                isSelected && thread.color
                  ? {
                      backgroundColor: thread.color,
                      // The ONE suppression DARK-7b's zero-occurrence assertion
                      // carries, named here rather than hidden. No token can
                      // express this: every role `-foreground` goes DARK in dark
                      // mode, because every role solid gets lighter — but
                      // `thread.color` is a fixed value from the data and does
                      // not move, so its ink must stay light in both themes.
                      // Inventing an always-white token for one call site is the
                      // vocabulary bloat DARK-2 rejected; `--scrim` is the same
                      // shape of need in the opposite direction and earned a
                      // token because it had fifteen sites.
                      // eslint-disable-next-line vale/no-raw-colour-value
                      color: '#fff'
                    }
                  : undefined
              }
            >
              {/* Thread color indicator */}
              {thread.color && (
                <div
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: thread.color }}
                />
              )}

              {/* Thread icon */}
              {thread.icon && <span className="text-sm">{thread.icon}</span>}

              {/* Thread name */}
              <span>
                {thread.subject.length > 20
                  ? thread.subject.substring(0, 20) + '...'
                  : thread.subject}
              </span>

              {/* Unread count */}
              {hasUnread && (
                <span className="text-12px bg-card/20 inline-flex h-5 w-5 items-center justify-center rounded-full font-bold">
                  {thread.unreadCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Filter status */}
      {hasSelectedThreads && (
        <div className="text-muted-foreground mt-1 text-xs">
          Showing {Object.values(visibility).filter((v) => v === true).length}{' '}
          of {threads.length} thread{threads.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}
